export interface PlayerNameMatch {
  id: string;
  name: string;
}

export interface PlayerProfile {
  id: string;
  name: string;
  full_name: string;
  url: string;
  description: string;
  image_url: string;
  position: string;
  other_positions: string;
  date_of_birth: string;
  place_of_birth_city: string;
  place_of_birth_country: string;
  age: number;
  height: string;
  nationality: string;
  is_retired: boolean;
  foot: string;
  shirt_number: string;
  joined_on: string;
  contract_expires: string;
  signed_from: string;
  market_value: string;
  club_id: string;
  club_name: string;
  agent_name: string;
  updated_at: string;
}

export interface RatingData {
  rating: number;
  explanation: string;
  score?: number;
}

export interface PlayerData {
  // Overall player score (median of all category ratings)
  overall_player_score?: number;
  
  // Global article references for all sections
  article_references?: Record<string, string>;
  
  family?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  financial?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  career?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  personal_life?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  agent?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  personality?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  early_career_and_education?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  adverse_media?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  legal_and_criminal_history?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  sponsorships?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  // Legacy fields for backward compatibility
  news?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  performance?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  education?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  compliance?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  legal?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  medical?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  commercial?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  prediction?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  comparables?: {
    summary?: string;
    rating?: RatingData;
    article_references?: Record<string, string>;
  };
  debug?: {
    summary?: {
      all_articles?: any[];
    };
  };
}

export interface PlayerInjury {
  player_id: string;
  season: string;
  injury: string;
  from_date: string;
  until_date: string | null;  // Null for ongoing injuries
  days: number;
  games_missed: number;
  updated_at: string;
}

export interface PlayerTransfer {
  id: string;
  player_id: string;
  from_club_id: string;
  from_club_name: string;
  to_club_id: string;
  to_club_name: string;
  transfer_date: string;
  season: string;
  is_loan: boolean;
  is_loan_end: boolean;
  updated_at: string;
  market_value: string;
  fee: string;
}

export interface PlayerAchievement {
  id: string;
  player_id: string;
  achievement_title: string;
  season_name: string;
  club_name: string;
  club_id: string;
  competition_name: string;
  competition_id: string;
  updated_at: string;
}

export interface PlayerStats {
  id: string;
  player_id: string;
  competition_id: string;
  competition_name: string;
  season_id: string;
  club_id: string;
  appearances: number;
  goals: number;
  assists: number;
  yellow_cards: number;
  red_cards: number;
  minutes_played: number;
  updated_at: string;
}

export interface CachedPlayer {
  id: string;
  name: string;
  profile: PlayerProfile;
  data: PlayerData;
  injuries?: PlayerInjury[];
  transfers?: PlayerTransfer[];
  achievements?: PlayerAchievement[];
  stats?: PlayerStats[];
}

export const PLAYER_SECTIONS = [
  { id: 'family', title: 'Family', path: 'family.summary' },
  { id: 'financial', title: 'Financial', path: 'financial.summary' },
  { id: 'career', title: 'Career', path: 'career.summary' },
  { id: 'personal_life', title: 'Personal Life', path: 'personal_life.summary' },
  { id: 'agent', title: 'Agent Information', path: 'agent.summary' },
  { id: 'personality', title: 'Personality', path: 'personality.summary' },
  { id: 'early_career_and_education', title: 'Early Career & Education', path: 'early_career_and_education.summary' },
  { id: 'adverse_media', title: 'Adverse Media', path: 'adverse_media.summary' },
  { id: 'legal_and_criminal_history', title: 'Legal & Criminal History', path: 'legal_and_criminal_history.summary' },
  { id: 'sponsorships', title: 'Sponsorships', path: 'sponsorships.summary' }
] as const;